import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { t } from 'ember-intl';
import { concat } from '@ember/helper';
import eq from 'madow-tech/helpers/eq';
import SpaceCard from 'madow-tech/components/space-card';
import ImageLightbox from 'madow-tech/components/image-lightbox';

export default class SuccessCases extends Component {
  @tracked lightboxOpen = false;
  @tracked lightboxImageUrl = '';
  @tracked lightboxImageIndex = 0;
  @tracked lightboxGallery: string[] = [];

  get cases() {
    return [
      {
        baseKey: 'success.idPlans',
        techStack: ['React', 'Nest.js'], // Update with actual if needed
      },
      {
        baseKey: 'success.feldico',
        techStack: ['Ember.js', 'Node.js', 'PostgreSQL', 'Elasticsearch', 'Docker'],
        gallery: [
          '/img/clients/feldico-1.webp',
          '/img/clients/feldico-2.webp',
          '/img/clients/feldico-3.webp',
        ],
      },
      // Add more projects here as details are provided
    ];
  }

  @action
  openLightbox(imageUrl: string, gallery: string[], index: number) {
    this.lightboxImageUrl = imageUrl;
    this.lightboxGallery = gallery;
    this.lightboxImageIndex = index;
    this.lightboxOpen = true;
    document.body.style.overflow = 'hidden';
    document.body.dataset.lightboxOpen = 'true';
  }

  @action
  closeLightbox() {
    this.lightboxOpen = false;
    document.body.style.overflow = '';
    document.body.removeAttribute('data-lightbox-open');
  }

  @action
  nextLightboxImage() {
    this.lightboxImageIndex = (this.lightboxImageIndex + 1) % this.lightboxGallery.length;
    this.lightboxImageUrl = this.lightboxGallery[this.lightboxImageIndex];
  }

  @action
  prevLightboxImage() {
    this.lightboxImageIndex = (this.lightboxImageIndex - 1 + this.lightboxGallery.length) % this.lightboxGallery.length;
    this.lightboxImageUrl = this.lightboxGallery[this.lightboxImageIndex];
  }

  @action
  scrollToContact() {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  @action
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.scrollToContact();
    }
  }

<template>
  <section id="success" class="py-20 md:py-28 overflow-x-hidden h-max">
    <div class="mx-auto max-w-7xl px-4">
      <h2 class="text-3xl md:text-4xl font-bold mb-4">{{t "success.title"}}</h2>
      <p class="mt-0 mb-8 max-w-3xl text-zinc-300 text-base md:text-lg">{{t "success.intro"}}</p>
      <div class="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-10 md:gap-16 py-12 md:py-16 mb-12 md:mb-16">
        <img
          src="/img/clients/feldico-logo.svg"
          alt="Feldico - Agritech marketplace platform client"
          width="200"
          height="64"
          loading="lazy"
          class="h-12 md:h-16"
        />
        <div class="w-2.5 h-2.5 bg-white rounded-full" aria-hidden="true"></div>
        <img
          src="/img/clients/idplans-logo.png"
          alt="ID Plans - Commercial real estate software client"
          height="48"
          loading="lazy"
          class="h-8 md:h-12 invert"
        />
        <div class="w-2.5 h-2.5 bg-white rounded-full"></div>
        <div
          class="group relative flex flex-col h-24 items-center cursor-pointer lg:mt-7"
          role="button"
          tabindex="0"
          {{on "click" this.scrollToContact}}
          {{on "keydown" this.handleKeyDown}}
        >
          <div class="h-24 md:h-[6.25rem] w-48 rounded-xl border-2 border-mars-500 bg-gradient-to-br from-mars-900/30 to-black/50 flex items-center justify-center relative overflow-hidden transition-all duration-300 hover:border-mars-400 hover:shadow-[0_0_20px_rgba(239,68,68,0.4)] hover:scale-105">
            <div class="absolute inset-0 bg-gradient-to-br from-mars-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div class="absolute inset-0 border-2 border-mars-400 rounded-xl opacity-0 group-hover:opacity-100 animate-pulse"></div>
            <span class="text-sm md:text-base font-bold text-mars-300 relative z-10 group-hover:text-mars-200 transition-colors">{{t "success.you"}}</span>
          </div>
          <span
            class="mt-3 opacity-0 -translate-y-2 text-xs font-semibold text-white whitespace-nowrap transition-all duration-300 ease-out group-hover:opacity-100 group-hover:translate-y-0"
          >
            {{t "success.letsReachOut"}}
          </span>
        </div>
      </div>
      <div class="flex flex-col gap-16 max-w-4xl mx-auto [&_.space-card]:!p-0">
        {{#each this.cases as |c|}}
          <SpaceCard
            @variant="v1"
            @title={{t (concat c.baseKey '.title')}}
            @titleClass="text-sm sm:text-lg text-nowrap"
            @titlePosition="right"
          >
            <article class="space-y-4">
              <div>
                <h4 class="text-sm font-bold text-zinc-100">{{t "success.labels.industry"}}</h4>
                <p class="text-sm text-zinc-300">{{t (concat c.baseKey '.industry')}}</p>
              </div>
              <div>
                <h4 class="text-sm font-bold text-zinc-100">{{t "success.labels.challenge"}}</h4>
                <p class="text-sm text-zinc-300">{{t (concat c.baseKey '.challenge')}}</p>
              </div>
              <div>
                <h4 class="text-sm font-bold text-zinc-100">{{t "success.labels.built"}}</h4>
                <p class="text-sm text-zinc-300">{{t (concat c.baseKey '.built')}}</p>
              </div>
              <div>
                <h4 class="text-sm font-bold text-zinc-100">{{t "success.labels.techStack"}}</h4>
                <p class="text-sm text-zinc-300">{{t (concat c.baseKey '.techStack')}}</p>
              </div>
              <div>
                <h4 class="text-sm font-bold text-zinc-100">{{t "success.labels.results"}}</h4>
                <p class="text-sm text-zinc-300">{{t (concat c.baseKey '.results')}}</p>
              </div>
              <div class="mt-4 italic text-sm text-zinc-400">
                "{{t (concat c.baseKey '.quote')}}"
                <span class="block font-medium text-zinc-300 mt-1">- {{t (concat c.baseKey '.author')}}</span>
              </div>
              {{#if c.gallery}}
                <div class="mt-6">
                  <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {{#each c.gallery as |imageUrl index|}}
                      <button
                        type="button"
                        class="success-gallery-thumbnail"
                        {{on "click" (fn this.openLightbox imageUrl c.gallery index)}}
                      >
                        <img
                          src={{imageUrl}}
                          alt="{{t (concat c.baseKey '.title')}} screenshot {{index}}"
                          loading="lazy"
                          decoding="async"
                        />
                        <div class="success-gallery-thumbnail-overlay">
                          <svg class="success-gallery-thumbnail-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </button>
                    {{/each}}
                  </div>
                </div>
              {{else if c.screenshot}}
                <div class="mt-6 rounded-lg overflow-hidden border border-zinc-700/50">
                  <img src={{c.screenshot}} alt="{{t (concat c.baseKey '.title')}} screenshot" class="w-full" />
                </div>
              {{/if}}
            </article>
          </SpaceCard>
        {{/each}}
      </div>
    </div>

    {{! Lightbox Component }}
    <ImageLightbox
      @isOpen={{this.lightboxOpen}}
      @imageUrl={{this.lightboxImageUrl}}
      @imageIndex={{this.lightboxImageIndex}}
      @gallery={{this.lightboxGallery}}
      @onClose={{this.closeLightbox}}
      @onNext={{this.nextLightboxImage}}
      @onPrev={{this.prevLightboxImage}}
    />
  </section>
</template>
